import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext, Validate } from "react-hook-form";
import TextFieldForm from "./TextFieldForm";
import { useTranslations } from "@fenetech/translations";
import { TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import { isNullOrWhiteSpace, ResolvePath } from "helpers/objects";

interface IProps {
    fieldName: string,  //field in useForm that holds the template text. This will still need formatted in the onsubmit for the form
    fieldsInvolvedInTemplate: string[], //other fields in useForm that are involved in the formatting. Causes the preview to update in real time
    formatPreviewText: (templateText: string) => string,
    readOnly: boolean,
    disabled?: boolean,
    previewLabel: string,
    editLabel: string,
    requiredRule?: { value: boolean, message: string },
    validateValue?: Validate<any> | Record<string, Validate<any>>
}

const EditPreviewTextFieldForm: React.FC<IProps> = ({ fieldName, fieldsInvolvedInTemplate, formatPreviewText, readOnly, disabled, previewLabel, editLabel, requiredRule, validateValue }: IProps) => {

    const tm = useTranslations();
    const { watch, getValues, formState: { errors } } = useFormContext();

    const [isPreview, setIsPreview] = useState<boolean>(true);
    const [previewText, setPreviewText] = useState<string>("");
    const [previewErrorMessage, setPreviewErrorMessage] = useState<string>("");

    const watchedFields = watch([fieldName, ...fieldsInvolvedInTemplate]);

    const handleTemplateChange = useCallback((templateText: string) => {
        setPreviewText(formatPreviewText(templateText));
    }, [formatPreviewText]);

    const updatePreviewErrorMessage = useCallback(() => {
        //The preview textfield is not controlled, so set it's error message to whatever the template's is
        const errorObj = ResolvePath(fieldName, errors);
        setPreviewErrorMessage(errorObj?.message ?? "");
    }, [fieldName, errors]);

    useEffect(() => {
        const template: string = getValues(fieldName);
        setPreviewText(formatPreviewText(template));
        updatePreviewErrorMessage();
    }, [watchedFields, getValues, fieldName, formatPreviewText, updatePreviewErrorMessage]);

    return <>
        <ToggleButtonGroup
            value={isPreview}
            exclusive
            disabled={disabled ?? false}
            onChange={(e, value) => { if (value !== null) setIsPreview(value) }}
            size="small"
        >
            <ToggleButton value={true}>
                <PreviewIcon /> {tm.Get("Preview")}
            </ToggleButton>
            <ToggleButton value={false}>
                <EditIcon /> {tm.Get("Edit")}
            </ToggleButton>
        </ToggleButtonGroup>

        <TextField
            value={previewText}
            label={previewLabel}
            InputProps={{ readOnly: true }}
            fullWidth
            multiline
            maxRows={10}
            disabled={disabled ?? false}
            error={!isNullOrWhiteSpace(previewErrorMessage)}
            helperText={previewErrorMessage}
            sx={{display: isPreview ? "inherit" : "none"}}
        />

        <TextFieldForm
            fieldName={fieldName}
            variant="outlined"
            readOnly={readOnly}
            disabled={disabled ?? false}
            label={editLabel}
            requiredRule={requiredRule}
            validateValue={validateValue}
            fullWidth
            multiline
            maxRows={10}
            onBlur={(e) => handleTemplateChange(e.target.value)}
            sx={{display: isPreview ? "none" : "inherit"}}
        />
    </>
}

export default EditPreviewTextFieldForm;